import _ from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { SearchIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, Input, Label, Paragraph, Subheading, tokens } from '@treams/treams-ui';

import Breadcrumbs from '../Breadcrumbs';
import Row from '../Row';
import trim from '../trim';
import { GroupSelection, SelectableGroupFilter } from './GroupSelector.types';

const Clip = styled(Box)`
	border-top: 1px solid ${tokens.border.subtle};
	overflow-y: auto;
`;

const Grid = styled(Flex)`
	position: relative;
	flex-direction: column;
`;

function StandaloneRow(props: {
	depth: number;
	group: graphql.Group;
	hasChildren: boolean;
	selected: (group: graphql.Group) => { selected: boolean; recursive: boolean; specific: boolean };
	onSelect?: (group: graphql.Group, recursive: boolean) => void;
	onDeselect?: (group: graphql.Group) => void;
	single?: boolean;
	canSelect: SelectableGroupFilter;
}) {
	const { selected, recursive, specific } = props.selected(props.group);
	const groupIsSelectable = props.canSelect(props.group);

	return (
		<Row
			group={props.group}
			hasChildren={props.hasChildren}
			recursive={recursive}
			disabled={!groupIsSelectable || (selected && !specific)}
			single={props.single ?? false}
			selected={selected}
			canSelect={groupIsSelectable}
			onSelect={props.onSelect ? (recursive) => props.onSelect!(props.group, recursive) : undefined}
			onDeselect={specific && props.onDeselect ? () => props.onDeselect!(props.group) : undefined}
		/>
	);
}

function Subtree(props: {
	depth: number;
	groups: graphql.Group[];
	parent?: string;
	root?: boolean;
	selected: (group: graphql.Group) => { selected: boolean; recursive: boolean; specific: boolean };
	onSelect?: (group: graphql.Group, recursive: boolean) => void;
	onDeselect?: (group: graphql.Group) => void;
	single?: boolean;
	canSelect: SelectableGroupFilter;
}) {
	const { t } = useTranslation();

	let firstTeam = true;

	return (
		<Box ml={props.depth > 0 ? 24 : 0}>
			{_.sortBy(props.groups, (group) => group.type !== graphql.GroupType.Department)
				.filter(
					(group) =>
						group.parent?.id === props.parent ||
						(props.root && group.id !== props.parent && !group.parent),
				)
				.map((group) => {
					const { selected, recursive, specific } = props.selected(group);
					const groupIsSelectable = props.canSelect(group);
					const isFirstTeam = firstTeam && group.type === graphql.GroupType.Team;

					if (isFirstTeam) {
						firstTeam = false;
					}

					const teamMargin = props.depth === 0 && !isFirstTeam ? 0 : -32;

					return (
						<Box key={group.id} ml={group.type === graphql.GroupType.Team ? teamMargin : 0}>
							{isFirstTeam && (
								<Label px={2} mt={3} ml={32}>
									{t('groups.selector.heading.teams')}
								</Label>
							)}
							<Row
								group={group}
								hasChildren={props.groups.some((child) => child.parent?.id === group.id)}
								recursive={recursive}
								disabled={!groupIsSelectable || (selected && !specific)}
								single={props.single ?? false}
								selected={selected}
								canSelect={props.canSelect(group)}
								onSelect={
									props.onSelect ? (recursive) => props.onSelect!(group, recursive) : undefined
								}
								onDeselect={
									specific && props.onDeselect ? () => props.onDeselect!(group) : undefined
								}
							>
								<Subtree
									depth={props.depth + 1}
									groups={props.groups}
									parent={group.id}
									onSelect={props.onSelect}
									onDeselect={props.onDeselect}
									single={props.single}
									canSelect={props.canSelect}
									selected={props.selected}
								/>
							</Row>
						</Box>
					);
				})}
		</Box>
	);
}

export function GroupSelectorNewModal(props: {
	allGroups: graphql.Group[];
	canSelect: SelectableGroupFilter;
	hasExplicitSelection: boolean;
	onClose: () => void;
	onSelect: (group: graphql.Group, recursive: boolean) => void;
	onDeselect: (group: graphql.Group) => void;
	single: boolean;
	groupsInSelection: graphql.Group[];
	groupSelection: GroupSelection[];
}) {
	const { allGroups, canSelect } = props;

	const rootGroup = useMemo(() => {
		return allGroups.find((group) => group.type === graphql.GroupType.Department && !group.parent);
	}, [allGroups]);

	const { t } = useTranslation();
	const [start, setStart] = useState<graphql.Group | undefined>(rootGroup);
	const [query, setQuery] = useState('');
	const [loading, setLoading] = useState(false);

	const startHasChildren = !!(start && allGroups.find((g) => g.parent?.id === start.id));

	const selectableGroupTree = useMemo(() => {
		const tree = trim(allGroups, canSelect);
		if (rootGroup) {
			tree.push(rootGroup);
		}
		return tree;
	}, [allGroups, canSelect, rootGroup]);

	useDebounce(
		() => {
			if (query === '') {
				setLoading(false);

				return;
			}

			const match = selectableGroupTree.find((group) =>
				group.name.toLowerCase().includes(query.toLowerCase()),
			);

			if (match) {
				if (allGroups.some((group) => group.parent?.id === match.id)) {
					setStart(match);
				} else {
					const newStart = match.parent
						? allGroups.find((g) => g.id === match.parent?.id)
						: undefined;
					setStart(newStart);
				}
			} else {
				setStart(undefined);
			}

			setLoading(false);
		},
		200,
		[query],
	);

	const breadcrumbs = useMemo(() => {
		const breadcrumbs: graphql.Group[] = [];
		const visited = new Set();

		let needle: graphql.Group | undefined = start;

		while (needle !== undefined) {
			if (visited.has(needle)) {
				break;
			}

			visited.add(needle);

			const match = selectableGroupTree.find((group) => group.id === needle?.id);
			if (match) {
				breadcrumbs.push(match);
				needle = match.parent || undefined;
			}
		}

		breadcrumbs.reverse();

		return breadcrumbs;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [start]);

	// While searching we show a flat, name-filtered list across the whole tree
	// instead of navigating into branches, so a query actually narrows the list.
	const searchResults = useMemo(() => {
		if (query === '') {
			return [];
		}

		const needle = query.toLowerCase();
		const seen = new Set<string>();

		return selectableGroupTree.filter((group) => {
			if (seen.has(group.id) || !group.name.toLowerCase().includes(needle)) {
				return false;
			}

			seen.add(group.id);

			return true;
		});
	}, [query, selectableGroupTree]);

	const isRowSelected = (group: graphql.Group) => ({
		selected:
			props.hasExplicitSelection && props.groupsInSelection.some((match) => group.id === match.id),
		specific:
			props.hasExplicitSelection &&
			(props.groupSelection || []).some((match) => match.group.id === group.id),
		recursive:
			(props.hasExplicitSelection &&
				(props.groupSelection || []).find((match) => match.group.id === group.id)?.recursive) ??
			false,
	});

	return (
		<Grid width="800px" height="600px">
			<Flex p={3}>
				<Box width={400} flexGrow={0} flexShrink={0}>
					<Input
						autoFocus
						search
						loading={loading}
						value={query}
						onChange={(query) => {
							if (query === '') {
								setStart(rootGroup);
							}

							setQuery(query);
						}}
						icon={<SearchIcon />}
						placeholder={t('groups.selector.search.placeholder')}
					/>
				</Box>
				<Box flexGrow={1} />
				<Button
					type="primary"
					title={t('groups.selector.search.done')}
					flexGrow={0}
					flexShrink={0}
					ml={2}
					onClick={props.onClose}
				/>
			</Flex>

			<Box px={4} flexGrow={0} flexShrink={0} mb={2}>
				{query === '' && (
					<Breadcrumbs
						components={breadcrumbs}
						onChange={(id) => {
							setQuery('');
							setStart(selectableGroupTree.find((g) => g.id === id));
						}}
					/>
				)}
			</Box>

			{query !== '' ? (
				searchResults.length === 0 ? (
					<Clip py={3} px={4}>
						<Subheading>{t('groups.selector.heading.notFound')}</Subheading>
						<Paragraph>{t('groups.selector.notFound')}</Paragraph>
					</Clip>
				) : (
					<Clip>
						<Box ml={-32}>
							{searchResults.map((group) => (
								<StandaloneRow
									key={group.id}
									group={group}
									depth={0}
									hasChildren={allGroups.some((child) => child.parent?.id === group.id)}
									selected={isRowSelected}
									single={props.single}
									canSelect={props.canSelect}
									onSelect={props.onSelect}
									onDeselect={props.onDeselect}
								/>
							))}
						</Box>
					</Clip>
				)
			) : !start ? (
				<Clip py={3} px={4}>
					<Subheading>{t('groups.selector.heading.notFound')}</Subheading>
					<Paragraph>{t('groups.selector.notFound')}</Paragraph>
				</Clip>
			) : (
				<Clip>
					{start === rootGroup &&
						allGroups.some((group) => group.type === graphql.GroupType.Team) && (
							<Label px={2} mt={2}>
								{t('groups.selector.heading.departments')}
							</Label>
						)}
					<Box ml={-32}>
						<StandaloneRow
							group={start}
							depth={0}
							hasChildren={startHasChildren}
							selected={isRowSelected}
							single={props.single}
							canSelect={props.canSelect}
							onSelect={props.onSelect}
							onDeselect={props.onDeselect}
						/>
					</Box>
					<Subtree
						depth={0}
						groups={selectableGroupTree}
						parent={start.id}
						root={breadcrumbs.length === 1}
						selected={isRowSelected}
						single={props.single}
						canSelect={props.canSelect}
						onSelect={props.onSelect}
						onDeselect={props.onDeselect}
					/>
				</Clip>
			)}
		</Grid>
	);
}
